interface TaskServiceConfig {
  url: string;
  socketUrl: string;
  apiKey: string;
}

interface VersionConfig {
  branch: string;
  commit: string;
  time: string;
}

interface SiteConfig {
  mnem: string;
  name: string;
  supportNumber: string;
}

interface FirestoreConfig {
  projectId: null | string;
  apiKey?: string;
  appId?: string;
  authDomain?: string;
  databaseURL?: string;
  measurementId?: string;
  messagingSenderId?: string;
  storageBucket?: string;
  persistenceEnabled?: boolean;
  diligent__EmulatorIp?: string;
  simServer?: string;
  diligentApiHost?: string;
  siteName?: string;
}

interface ElasticConfig {
  url: string;
  index: string;
  creds: string;
  // minNetTicks: number;
  apiKeyExp: number;  // expiration timestamp for the creds
}

interface Config {
  taskService: null | TaskServiceConfig;
  kioskSettings: boolean;
  version: null | VersionConfig;
  site: null | SiteConfig;
  firestore: null | FirestoreConfig;
  mbotElastic: null | ElasticConfig;  // Elastic config from MBot backend (preferred)
  firestoreElastic: null | ElasticConfig;  // Elastic config from Firestore (backup)
  schedulerUrl: string;
  uiSimServer: string;
  lockProxyHost: string;
  lockProxyHostEncrypted: string;
  diligentApiHost: string;
  secureDelivery: boolean;
  userFacingBatching: boolean;
  firestoreRefreshMinutes: number;
  loadUrl: null | string;
  loadTicks: number;
}

let _config: Config;
let _queryParams = '',
  _scrubbedQueryParams = '';

function getDiligentApiHost(config: Config): string {
  let url;
  const isSim = config.site?.mnem === 'sim';
  const isEmulator = config.firestore?.projectId?.endsWith('-emulator');

  if (config.taskService) {
    url = new URL('v1', config.taskService.url);
  } else if (isSim) {
    url = new URL('api/v1', config.uiSimServer);
  } else if (isEmulator) {
    return 'http://localhost:9000/api/v1';
  } else {
    url = new URL('api/v1', config.schedulerUrl);
  }

  return url.href;
}

function getErrorMessage(key: string) {
  return `Missing environment variable: ${key}`;
}

function loadTaskServiceConfig(): TaskServiceConfig {
  const taskServiceConfig: TaskServiceConfig = {
    url: '',
    socketUrl: '',
    apiKey: '',
  };

  if (process.env.REACT_APP_TASK_SERVICE_URL) {
    taskServiceConfig.url = process.env.REACT_APP_TASK_SERVICE_URL;
  } else {
    throw new Error(getErrorMessage('REACT_APP_TASK_SERVICE_URL'));
  }

  if (process.env.REACT_APP_TASK_SERVICE_SOCKET_URL) {
    taskServiceConfig.socketUrl = process.env.REACT_APP_TASK_SERVICE_SOCKET_URL;
  } else {
    throw new Error(getErrorMessage('REACT_APP_TASK_SERVICE_SOCKET_URL'));
  }

  if (process.env.REACT_APP_TASK_SERVICE_API_KEY) {
    taskServiceConfig.apiKey = process.env.REACT_APP_TASK_SERVICE_API_KEY;
  } else {
    throw new Error(getErrorMessage('REACT_APP_TASK_SERVICE_API_KEY'));
  }

  return taskServiceConfig;
}

function loadVersionConfig(): VersionConfig {
  const versionConfig: VersionConfig = {
    branch: process.env.REACT_APP_VERSION_BRANCH || 'latest',
    time: process.env.REACT_APP_VERSION_TIME || 'now',
    commit: '',
  };

  if (!process.env.REACT_APP_VERSION_COMMIT) {
    throw new Error(getErrorMessage('REACT_APP_VERSION_COMMIT'));
  } else {
    versionConfig.commit = process.env.REACT_APP_VERSION_COMMIT;
  }

  return versionConfig;
}

function loadSiteConfig(): SiteConfig {
  const siteConfig: SiteConfig = {
    mnem: '',
    name: '',
    supportNumber: '',
  };

  if (!process.env.REACT_APP_SITE_MNEM) {
    throw new Error(getErrorMessage('REACT_APP_SITE_MNEM'));
  } else {
    siteConfig.mnem = process.env.REACT_APP_SITE_MNEM;
  }
  if (!process.env.REACT_APP_SITE_NAME) {
    throw new Error(getErrorMessage('REACT_APP_SITE_NAME'));
  } else {
    siteConfig.name = process.env.REACT_APP_SITE_NAME;
  }
  if (process.env.REACT_APP_SITE_SUPPORT_NUMBER) {
    siteConfig.supportNumber = process.env.REACT_APP_SITE_SUPPORT_NUMBER;
  }

  return siteConfig;
}

function loadFirestoreConfig(): FirestoreConfig {
  const firestoreConfig: FirestoreConfig = {
    projectId: null,
  };

  if (!process.env.REACT_APP_FIRESTORE_PROJECT_ID) {
    throw new Error(getErrorMessage('REACT_APP_FIRESTORE_PROJECT_ID'));
  } else {
    firestoreConfig.projectId = process.env.REACT_APP_FIRESTORE_PROJECT_ID;
  }
  if (process.env.REACT_APP_FIRESTORE_API_KEY) {
    firestoreConfig.apiKey = process.env.REACT_APP_FIRESTORE_API_KEY;
  }
  if (process.env.REACT_APP_FIRESTORE_APP_ID) {
    firestoreConfig.appId = process.env.REACT_APP_FIRESTORE_APP_ID;
  }
  if (process.env.REACT_APP_FIRESTORE_AUTH_DOMAIN) {
    firestoreConfig.authDomain = process.env.REACT_APP_FIRESTORE_AUTH_DOMAIN;
  }
  if (process.env.REACT_APP_FIRESTORE_DATABASE_URL) {
    firestoreConfig.databaseURL = process.env.REACT_APP_FIRESTORE_DATABASE_URL;
  }
  if (process.env.REACT_APP_FIRESTORE_MEASUREMENT_ID) {
    firestoreConfig.measurementId = process.env.REACT_APP_FIRESTORE_MEASUREMENT_ID;
  }
  if (process.env.REACT_APP_FIRESTORE_MESSAGING_SENDER_ID) {
    firestoreConfig.messagingSenderId = process.env.REACT_APP_FIRESTORE_MESSAGING_SENDER_ID;
  }
  if (process.env.REACT_APP_FIRESTORE_STORAGE_BUCKET) {
    firestoreConfig.storageBucket = process.env.REACT_APP_FIRESTORE_STORAGE_BUCKET;
  }
  if (firestoreConfig.projectId.endsWith('-emulator')) {
    firestoreConfig.persistenceEnabled = true;
    firestoreConfig.diligent__EmulatorIp = 'localhost';
  }
  if (
    process.env.REACT_APP_SITE_MNEM &&
    process.env.REACT_APP_SITE_MNEM === 'sim' &&
    process.env.REACT_APP_UI_SIM_SERVER
  ) {
    firestoreConfig.simServer = process.env.REACT_APP_UI_SIM_SERVER;
    firestoreConfig.diligentApiHost = `${process.env.REACT_APP_UI_SIM_SERVER}/api/v1`;
    firestoreConfig.siteName = process.env.REACT_APP_SITE_MNEM;
  }

  return firestoreConfig;
}


function loadConfig() {
  const config: Config = {
    taskService: null,
    kioskSettings: false,
    version: null,
    site: null,
    firestore: null,
    mbotElastic: null,
    firestoreElastic: null,
    uiSimServer: '',
    schedulerUrl: '',
    lockProxyHost: '',
    lockProxyHostEncrypted: '',
    diligentApiHost: '',
    secureDelivery: false,
    userFacingBatching: false,
    firestoreRefreshMinutes: 60 * 6, //6 hours
    loadUrl: '',
    loadTicks: Date.now(),
  };

  // Load task-service config
  if (!process.env.REACT_APP_TASK_SERVICE_ENABLED) {
    throw new Error(getErrorMessage('REACT_APP_TASK_SERVICE_ENABLED'));
  }
  if (process.env.REACT_APP_TASK_SERVICE_ENABLED === 'true') {
    config.taskService = loadTaskServiceConfig();
  }

  // Load scheduler url (for v1)
  if (!process.env.REACT_APP_SCHEDULER_URL) {
    throw new Error(getErrorMessage('REACT_APP_SCHEDULER_URL'));
  } else {
    config.schedulerUrl = process.env.REACT_APP_SCHEDULER_URL;
  }

  // Load kiosk settings feature flag
  if (!process.env.REACT_APP_KIOSK_SETTINGS_ENABLED) {
    throw new Error(getErrorMessage('REACT_APP_KIOSK_SETTINGS_ENABLED'));
  }
  if (process.env.REACT_APP_KIOSK_SETTINGS_ENABLED === 'true') {
    config.kioskSettings = true;
  }

  // Load secure delivery feature flag
  if (!process.env.REACT_APP_SECURE_DELIVERY_ENABLED) {
    throw new Error(getErrorMessage('REACT_APP_SECURE_DELIVERY_ENABLED'));
  }
  if (process.env.REACT_APP_SECURE_DELIVERY_ENABLED === 'true') {
    config.secureDelivery = true;
  }

  // Load user facting batching feature flag
  if (!process.env.REACT_APP_USER_FACING_BATCHING_ENABLED) {
    throw new Error(getErrorMessage('REACT_APP_USER_FACING_BATCHING_ENABLED'));
  }
  if (process.env.REACT_APP_USER_FACING_BATCHING_ENABLED === 'true') {
    config.userFacingBatching = true;
  }

  //coercing this to true because all sites have it enabled; time to embrace it.
  config.userFacingBatching = true;

  //LMR todo set these in sim by default
  // config.secureDelivery = true;

  // Load version config
  config.version = loadVersionConfig();

  // Load site config
  config.site = loadSiteConfig();

  // Load firestore config
  config.firestore = loadFirestoreConfig();

  // fields .mbotElastic and .elasticFromFirestore are loaded elsewhere.

  // Load ui sim server config
  if (!process.env.REACT_APP_UI_SIM_SERVER) {
    throw new Error(getErrorMessage('REACT_APP_UI_SIM_SERVER'));
  }
  config.uiSimServer = process.env.REACT_APP_UI_SIM_SERVER;

  // Load lock proxy
  if (!process.env.REACT_APP_LOCK_PROXY_HOST) {
    throw new Error(getErrorMessage('REACT_APP_LOCK_PROXY_HOST'));
  }
  config.lockProxyHost = process.env.REACT_APP_LOCK_PROXY_HOST;

  // Load lock proxy (encrypted)
  if (!process.env.REACT_APP_LOCK_PROXY_HOST_ENCRYPTED) {
    throw new Error(getErrorMessage('REACT_APP_LOCK_PROXY_HOST_ENCRYPTED'));
  }
  config.lockProxyHostEncrypted = process.env.REACT_APP_LOCK_PROXY_HOST_ENCRYPTED;

  config.diligentApiHost = getDiligentApiHost(config);

  config.loadUrl = window.location.href;

  //------------------------------------------------
  // config.firestore = {
  //   "apiKey": "AIzaSyAXGmn_SbcidoYm-HgFomxWHWnphghQcxc",
  //   ...
  //   "siteName": "siteNN"
  // };
  // config.site = {mnem:'siteNN', name:'siteNN LOCAL', supportNumber:'000-000-0000'};

  // //# config.diligentApiHost = "https://siteNN.diligentrobots.io/app/scheduler/api/v1";
  // //# config.uiSimServer = "https://siteNN.mbotsvc.diligentrobots.io";
  // config.uiSimServer = "http://192.168.0.231:3200";

  // config.schedulerUrl = "http://127.0.0.1:3200";
  //------------------------------------------------

  _config = config;
  return config;
}


function getConfig() {
  return _config;
}

function setConfig(newConfig: Config) {
  _config = newConfig;
}

function getLoadQueryParams(scrubbed: boolean) {
  if (!_queryParams) {
    let config = getConfig();
    if (config) {
      let url = config.loadUrl || '';
      url = url.slice(url.indexOf('://') + 3);
      let queryIdx = url.indexOf('?');
      if (queryIdx >= 0) url = url.slice(queryIdx + 1);

      _queryParams = _scrubbedQueryParams = url;

      //for _scrubbedQueryParams, obscure the last four digits of the opt, if present
      const otpEq = 'otp=';
      let otpEqIdx = url.indexOf(otpEq);
      if (otpEqIdx >= 0) {
        const optStartIdx = otpEqIdx + otpEq.length;
        let spanEnd = url.indexOf('&', optStartIdx);
        if (spanEnd < 0) spanEnd = url.length;
        let spanStart = Math.max(spanEnd - 4, optStartIdx);
        _scrubbedQueryParams = url.slice(0, spanStart) + '*'.repeat(spanEnd - spanStart) + url.slice(spanEnd);
      }
    }
  }
  return scrubbed ? _scrubbedQueryParams : _queryParams;
}

export {
  getConfig,
  getLoadQueryParams,
  getDiligentApiHost,
  loadConfig,
  loadSiteConfig,
  loadTaskServiceConfig,
  loadVersionConfig,
  setConfig,
};
